/**
 * Secciones avanzadas de la pestaña Análisis: hallazgos automáticos, anatomía del post ganador,
 * dependencia de virales (Pareto), frecuencia y constancia, momentum con pronóstico y
 * comparación contra la mediana de la red. Los cálculos viven en analysisExtras.js.
 */
import React, { useMemo, useState } from 'react';
import {
    CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis,
} from 'recharts';
import { ink, studio } from '../admin/studioUi';
import NetworkDot from './NetworkDot';
import InfoTip from './InfoTip';
import MobileCards from './MobileCards';
import { rt } from './repoTable';
import { NETWORK_COLORS, formatNumber, formatPct } from './socialRepoUtils';

const TONES = {
    good: { color: '#137333', bg: '#e6f4ea', icon: '▲' },
    bad: { color: '#c5221f', bg: '#fce8e6', icon: '▼' },
    info: { color: '#4527a0', bg: '#f4eefc', icon: '●' },
};
const SECTION_LABELS = { comparativa: 'Comparativa', contenido: 'Contenido', tiempos: 'Tiempos', tendencias: 'Tendencias' };

export function Card({ title, info, infoWhat, infoHow, hint, actions, children }) {
    return (
        <section style={studio.card}>
            <div style={styles.cardHead}>
                <div style={{ minWidth: 0 }}>
                    <h3 style={styles.cardTitle}>{title}{(info || infoWhat) && <InfoTip term={info} what={infoWhat} how={infoHow} />}</h3>
                    {hint && <p style={studio.hint}>{hint}</p>}
                </div>
                {actions}
            </div>
            {children}
        </section>
    );
}

const times = v => (v == null ? '—' : `${v.toFixed(2)}×`);
const liftColor = v => (v == null ? ink.text : v >= 1.15 ? '#137333' : v <= 0.85 ? ink.danger : ink.text);

/** Hallazgos automáticos con enlace a la sección donde se revisan. */
export function InsightsPanel({ insights, onGo }) {
    return (
        <Card
            title="Hallazgos del periodo"
            infoWhat="Frases generadas automáticamente con lo más relevante de los datos filtrados."
            infoHow="Se revisan cambios fuertes de tasa contra el periodo anterior (≥ 30%), rasgos de texto que rinden 1.4× o más, dependencia de virales, mejor franja, relación frecuencia-rendimiento, huecos de 3+ semanas sin publicar, tendencias sostenidas (R² ≥ 0.5) y perfiles 2× mejores que su red. Se ordenan por magnitud."
            hint="Se recalculan con los filtros, la métrica elegida y lo que omitas."
        >
            {insights.length ? (
                <ul style={styles.insightList}>
                    {insights.map((item, index) => {
                        const tone = TONES[item.tone] || TONES.info;
                        return (
                            <li key={index} style={{ ...styles.insight, backgroundColor: tone.bg }}>
                                <span style={{ color: tone.color, fontSize: '0.7rem', marginTop: 3 }}>{tone.icon}</span>
                                <span style={{ flex: 1 }}>{item.text}</span>
                                {onGo && item.section && (
                                    <button type="button" style={styles.goBtn} onClick={() => onGo(item.section)}>
                                        Ver {SECTION_LABELS[item.section]?.toLowerCase()}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p style={studio.hint}>No hay hallazgos destacados con estos filtros (hacen falta más posts o variaciones más marcadas).</p>
            )}
        </Card>
    );
}

/** Rasgos del texto que acompañan a los posts con mejor rendimiento. */
export function AnatomySection({ anatomy, formatFocus, metricLabel, narrow }) {
    return (
        <Card
            title="Anatomía del post ganador"
            infoWhat="Compara posts según rasgos de su texto para ver cuáles acompañan a un mejor rendimiento."
            infoHow={`Para cada rasgo se calcula la mediana de ${metricLabel} de los posts que lo tienen y se divide entre la mediana de todos los posts («lift»: 1.30× = rinde 30% más que el post típico). Es una asociación, no prueba causa; revisa el número de posts.`}
            hint="Verde = rinde más que el post típico; rojo = rinde menos."
        >
            <div style={{ ...styles.anatomyGrid, gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                {anatomy.map(feature => (
                    <div key={feature.id} style={styles.anatomyCard}>
                        <div style={styles.anatomyTitle}>{feature.label}</div>
                        {feature.values.map(v => (
                            <div key={v.value} style={styles.anatomyRow}>
                                <span style={{ flex: 1, minWidth: 0 }}>{v.value} <span style={styles.muted}>({formatNumber(v.n)})</span></span>
                                <span style={styles.muted}>{formatFocus(v.median)}</span>
                                <span style={{ ...styles.lift, color: liftColor(v.lift) }}>{times(v.lift)}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </Card>
    );
}

/** Curva de Pareto por red: qué parte del resultado aportan los mejores posts. */
export function ParetoSection({ pareto, metricLabel, narrow }) {
    return (
        <Card
            title="Dependencia de virales"
            infoWhat="Muestra si el resultado depende de unos cuantos posts muy exitosos o se reparte entre muchos."
            infoHow={`Se ordenan los posts de mayor a menor ${metricLabel} y se acumula su participación. La diagonal es un reparto perfectamente parejo; mientras más se aleja la curva hacia arriba, más concentrado está el resultado. «Top 20%» = parte del total que generan el 20% de los mejores posts.`}
        >
            <div style={styles.paretoChips}>
                {pareto.map(p => (
                    <span key={p.red} style={styles.paretoChip}>
                        <NetworkDot red={p.red} />{p.red}: top 20% = <strong>{formatPct(p.top20, 0)}</strong>
                    </span>
                ))}
            </div>
            <div style={{ width: '100%', height: narrow ? 220 : 260 }}>
                <ResponsiveContainer>
                    <LineChart margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={ink.line} />
                        <XAxis type="number" dataKey="posts" domain={[0, 1]} tickFormatter={v => formatPct(v, 0)} tick={{ fontSize: 11, fill: ink.muted }} />
                        <YAxis type="number" domain={[0, 1]} tickFormatter={v => formatPct(v, 0)} tick={{ fontSize: 11, fill: ink.muted }} width={44} />
                        <Tooltip formatter={(v, name) => [formatPct(v, 0), name]} labelFormatter={v => `${formatPct(v, 0)} de los posts`} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} stroke={ink.lineStrong} strokeDasharray="4 4" />
                        {pareto.map(p => (
                            <Line key={p.red} data={p.curve} dataKey="share" name={p.red} type="monotone" stroke={NETWORK_COLORS[p.red] || ink.accent} strokeWidth={2} dot={false} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
}

/** Frecuencia de publicación contra rendimiento por post, y constancia. */
export function FrequencySection({ frequency, formatFocus, metricLabel, narrow, onToggleExclude }) {
    const [sort, setSort] = useState('perWeek');
    const rows = useMemo(() => [...frequency.profiles].sort((a, b) => (sort === 'consistency' ? a.consistency - b.consistency : b[sort] - a[sort])), [frequency.profiles, sort]);
    const points = frequency.profiles.filter(p => p.posts >= 3);
    const corr = frequency.correlation;
    return (
        <Card
            title="Frecuencia, constancia y rendimiento"
            infoWhat="¿Publicar más seguido ayuda o desgasta? ¿Qué tan constante es cada perfil?"
            infoHow={`Posts por semana = posts del periodo ÷ semanas del periodo (${frequency.weeks}). Constancia = % de semanas con al menos un post. Mayor hueco = días máximos entre dos posts. La correlación (Pearson) compara posts por semana contra la mediana de ${metricLabel} por post entre perfiles con 3 o más posts.`}
            hint={corr == null
                ? 'Se necesitan más perfiles con publicaciones para medir la relación.'
                : `Correlación frecuencia ↔ rendimiento: ${corr.toFixed(2)} (${Math.abs(corr) < 0.2 ? 'sin relación clara' : corr > 0 ? 'publicar más va con mejor rendimiento' : 'publicar más va con peor rendimiento por post'}).`}
        >
            <div style={{ display: 'grid', gap: 18, gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 2fr) minmax(0, 3fr)' }}>
                <div style={{ width: '100%', height: narrow ? 240 : 300 }}>
                    <ResponsiveContainer>
                        <ScatterChart margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                            <CartesianGrid stroke={ink.line} />
                            <XAxis type="number" dataKey="perWeek" name="Posts/semana" tick={{ fontSize: 11, fill: ink.muted }} tickFormatter={v => v.toFixed(1)} />
                            <YAxis type="number" dataKey="median" name="Mediana" tick={{ fontSize: 11, fill: ink.muted }} width={48} scale="sqrt" />
                            <Tooltip
                                content={({ payload }) => {
                                    const p = payload?.[0]?.payload;
                                    if (!p) return null;
                                    return (
                                        <div style={styles.tooltip}>
                                            <strong>{p.Perfil}</strong> · {p.Red}
                                            <div>{p.perWeek.toFixed(1)} posts/semana</div>
                                            <div>Mediana: {formatFocus(p.median)}</div>
                                        </div>
                                    );
                                }}
                            />
                            {Object.keys(NETWORK_COLORS).map(red => (
                                <Scatter key={red} data={points.filter(p => p.Red === red)} fill={NETWORK_COLORS[red]} fillOpacity={0.7} />
                            ))}
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
                <div>
                    <div style={styles.sortRow}>
                        Ordenar por:
                        {[['perWeek', 'Frecuencia'], ['median', 'Rendimiento'], ['consistency', 'Menos constantes'], ['maxGap', 'Mayor hueco']].map(([id, label]) => (
                            <button key={id} type="button" onClick={() => setSort(id)} style={{ ...styles.sortBtn, ...(sort === id ? styles.sortBtnOn : {}) }}>{label}</button>
                        ))}
                    </div>
                    {narrow ? (
                        <MobileCards
                            items={rows}
                            keyOf={r => r.key}
                            titleOf={r => <><NetworkDot red={r.Red} />{r.Perfil}</>}
                            subtitleOf={r => r.Red}
                            metricsOf={r => [
                                { label: 'Posts/sem.', value: r.perWeek.toFixed(1) },
                                { label: 'Constancia', value: formatPct(r.consistency, 0) },
                                { label: 'Mayor hueco', value: `${r.maxGap} d`, color: r.maxGap >= 21 ? ink.danger : undefined },
                                { label: 'Mediana', value: formatFocus(r.median) },
                                { label: 'Posts', value: formatNumber(r.posts) },
                            ]}
                            actionOf={onToggleExclude ? (r => <button type="button" style={styles.omitBtn} onClick={() => onToggleExclude([r.key])}>Omitir</button>) : undefined}
                        />
                    ) : (
                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                            <table style={rt.table}>
                                <thead>
                                    <tr>
                                        {['Perfil', 'Posts/sem.', 'Constancia', 'Mayor hueco', 'Mediana'].map(h => (
                                            <th key={h} style={{ ...rt.th, textAlign: h === 'Perfil' ? 'left' : 'right', position: 'sticky', top: 0 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.key}>
                                            <td style={rt.td}><span style={styles.redCell}><NetworkDot red={r.Red} />{r.Perfil}</span></td>
                                            <td style={styles.numTd}>{r.perWeek.toFixed(1)}</td>
                                            <td style={styles.numTd}>
                                                <span style={styles.barWrap}><span style={{ ...styles.bar, width: `${Math.round(r.consistency * 100)}%` }} /></span>
                                                {formatPct(r.consistency, 0)}
                                            </td>
                                            <td style={{ ...styles.numTd, color: r.maxGap >= 21 ? ink.danger : ink.text }}>{r.maxGap} d</td>
                                            <td style={styles.numTd}>{formatFocus(r.median)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

function Sparkline({ values, color }) {
    const max = Math.max(1, ...values);
    const step = values.length > 1 ? 100 / (values.length - 1) : 100;
    const points = values.map((v, i) => `${(i * step).toFixed(1)},${(28 - (v / max) * 26).toFixed(1)}`).join(' ');
    return (
        <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width: 90, height: 26, display: 'block' }}>
            <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

const confidence = r2 => (r2 == null ? '—' : r2 >= 0.7 ? 'Alta' : r2 >= 0.4 ? 'Media' : 'Baja');

/** Tendencia sostenida por perfil (regresión lineal) y pronóstico del mes siguiente. */
export function MomentumSection({ rows, formatFocus, metricLabel, narrow, onToggleExclude }) {
    const [dir, setDir] = useState('up');
    const sorted = useMemo(() => [...rows]
        .filter(r => r.slopePct != null)
        .sort((a, b) => (dir === 'up' ? b.slopePct - a.slopePct : a.slopePct - b.slopePct)), [rows, dir]);
    return (
        <Card
            title="Momentum y pronóstico"
            infoWhat={`Detecta qué perfiles vienen creciendo o cayendo de forma sostenida en ${metricLabel} y proyecta el siguiente mes.`}
            infoHow="Regresión lineal sobre los últimos 6 meses (hasta el cierre del periodo). Tendencia = pendiente ÷ promedio mensual (cambio % por mes). Confianza = R²: qué tan bien explica la línea los datos (alta ≥ 0.7, media ≥ 0.4). Pronóstico = la línea extendida un mes; es orientativo, no considera campañas ni estacionalidad."
            hint="Los meses sin publicaciones cuentan como cero."
            actions={(
                <div style={styles.sortRow}>
                    <button type="button" onClick={() => setDir('up')} style={{ ...styles.sortBtn, ...(dir === 'up' ? styles.sortBtnOn : {}) }}>Al alza</button>
                    <button type="button" onClick={() => setDir('down')} style={{ ...styles.sortBtn, ...(dir === 'down' ? styles.sortBtnOn : {}) }}>A la baja</button>
                </div>
            )}
        >
            {narrow ? (
                <MobileCards
                    items={sorted}
                    keyOf={r => r.key}
                    titleOf={r => <><NetworkDot red={r.Red} />{r.Perfil}</>}
                    subtitleOf={r => <Sparkline values={r.series} color={NETWORK_COLORS[r.Red] || ink.accent} />}
                    metricsOf={r => [
                        { label: 'Tendencia', value: `${r.slopePct >= 0 ? '+' : ''}${formatPct(r.slopePct, 0)}/mes`, color: r.slopePct >= 0 ? '#137333' : ink.danger },
                        { label: 'Confianza', value: confidence(r.r2) },
                        { label: 'Pronóstico', value: formatFocus(r.forecast) },
                    ]}
                    actionOf={onToggleExclude ? (r => <button type="button" style={styles.omitBtn} onClick={() => onToggleExclude([r.key])}>Omitir</button>) : undefined}
                />
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {['Perfil', 'Últimos 6 meses', 'Tendencia', 'Confianza', 'Último mes', 'Pronóstico'].map(h => (
                                    <th key={h} style={{ ...rt.th, textAlign: h === 'Perfil' || h === 'Últimos 6 meses' ? 'left' : 'right' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(r => (
                                <tr key={r.key}>
                                    <td style={rt.td}><span style={styles.redCell}><NetworkDot red={r.Red} />{r.Perfil}</span></td>
                                    <td style={rt.td}><Sparkline values={r.series} color={NETWORK_COLORS[r.Red] || ink.accent} /></td>
                                    <td style={{ ...styles.numTd, fontWeight: 700, color: r.slopePct >= 0 ? '#137333' : ink.danger }}>
                                        {r.slopePct >= 0 ? '▲ +' : '▼ '}{formatPct(r.slopePct, 0)}/mes
                                    </td>
                                    <td style={styles.numTd} title={`R² = ${r.r2 == null ? '—' : r.r2.toFixed(2)}`}>{confidence(r.r2)}</td>
                                    <td style={styles.numTd}>{formatFocus(r.last)}</td>
                                    <td style={styles.numTd}>{formatFocus(r.forecast)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

/** Cada perfil contra el perfil típico (mediana) de su misma red. */
export function BenchmarkSection({ rows, narrow, onToggleExclude }) {
    const sorted = useMemo(() => [...rows]
        .filter(r => r.posts >= 2)
        .sort((a, b) => (b.rateVsNetwork ?? -1) - (a.rateVsNetwork ?? -1)), [rows]);
    return (
        <Card
            title="Cada perfil contra su red"
            infoWhat="Compara a cada perfil con el perfil típico de su misma red, porque una buena tasa en TikTok no es lo mismo que en LinkedIn."
            infoHow="Tasa del perfil ÷ mediana de las tasas de los perfiles de la misma red (y lo mismo con interacciones por post). 1.00× = igual que el típico; 2.00× = el doble. Solo perfiles con 2 o más posts."
        >
            {narrow ? (
                <MobileCards
                    items={sorted}
                    keyOf={r => r.key}
                    titleOf={r => <><NetworkDot red={r.Red} />{r.Perfil}</>}
                    subtitleOf={r => `${r.Red} · ${r.peers} perfiles en la red`}
                    metricsOf={r => [
                        { label: 'Tasa', value: formatPct(r.rate, 2) },
                        { label: 'vs. red', value: times(r.rateVsNetwork), color: liftColor(r.rateVsNetwork) },
                        { label: 'Int./post vs red', value: times(r.perPostVsNetwork), color: liftColor(r.perPostVsNetwork) },
                    ]}
                    actionOf={onToggleExclude ? (r => <button type="button" style={styles.omitBtn} onClick={() => onToggleExclude([r.key])}>Omitir</button>) : undefined}
                />
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {['Perfil', 'Red', 'Posts', 'Tasa', 'Tasa vs. red', 'Int./post', 'Int./post vs. red'].map(h => (
                                    <th key={h} style={{ ...rt.th, textAlign: h === 'Perfil' || h === 'Red' ? 'left' : 'right' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(r => (
                                <tr key={r.key}>
                                    <td style={{ ...rt.td, fontWeight: 600 }}>{r.Perfil}</td>
                                    <td style={rt.td}><span style={styles.redCell}><NetworkDot red={r.Red} />{r.Red}</span></td>
                                    <td style={styles.numTd}>{formatNumber(r.posts)}</td>
                                    <td style={styles.numTd}>{formatPct(r.rate, 2)}</td>
                                    <td style={{ ...styles.numTd, fontWeight: 700, color: liftColor(r.rateVsNetwork) }}>{times(r.rateVsNetwork)}</td>
                                    <td style={styles.numTd}>{formatNumber(r.perPost)}</td>
                                    <td style={{ ...styles.numTd, fontWeight: 700, color: liftColor(r.perPostVsNetwork) }}>{times(r.perPostVsNetwork)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

const styles = {
    cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
    cardTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 600, color: ink.text, display: 'flex', alignItems: 'center', flexWrap: 'wrap' },
    insightList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
    insight: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 10, fontSize: '0.88rem', color: ink.text, lineHeight: 1.45, flexWrap: 'wrap' },
    goBtn: {
        background: 'none',
        border: 'none',
        padding: 0,
        color: ink.accent,
        fontWeight: 600,
        fontSize: '0.78rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    anatomyGrid: { display: 'grid', gap: 12 },
    anatomyCard: { padding: '12px 14px', borderRadius: 12, backgroundColor: ink.wash, display: 'flex', flexDirection: 'column', gap: 6 },
    anatomyTitle: { fontWeight: 700, fontSize: '0.86rem', color: ink.text, marginBottom: 2 },
    anatomyRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.84rem', color: ink.text },
    lift: { fontWeight: 700, minWidth: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    muted: { color: ink.faint, fontSize: '0.78rem' },
    paretoChips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
    paretoChip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, backgroundColor: ink.wash, fontSize: '0.8rem', color: ink.text },
    tooltip: {
        padding: '8px 10px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 8,
        fontSize: '0.8rem',
        boxShadow: '0 6px 18px rgba(28, 29, 33, 0.12)',
    },
    sortRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: ink.faint, marginBottom: 8 },
    sortBtn: {
        padding: '4px 10px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.76rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
    },
    sortBtnOn: { borderColor: ink.accent, backgroundColor: ink.accentSoft, color: ink.accentText },
    redCell: { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: '0.86rem' },
    numTd: { ...rt.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    barWrap: { display: 'inline-block', width: 50, height: 6, borderRadius: 3, backgroundColor: ink.wash, marginRight: 8, verticalAlign: 'middle', overflow: 'hidden' },
    bar: { display: 'block', height: 6, backgroundColor: ink.accent },
    omitBtn: {
        padding: '3px 9px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.72rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
};
